import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { ThemeProvider } from "./components/ThemeProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import AuthCallback from "./pages/AuthCallback";
import { getPublicConfig } from "./lib/api";
import { DEFAULT_FAVICON_URL, resolveBrandAssetUrl } from "./lib/branding";
import { Toaster } from "./components/ui/sonner";
import { flAppRoutes, flPublicRoutes } from "./fulfillment/routes";

// Lazy-loaded routes (code-splitting)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPassword"));
const BlogListPage = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.BlogList })));
const BlogPostPage = lazy(() => import("./pages/BlogPage").then(m => ({ default: m.BlogPost })));
const StaticPage = lazy(() => import("./pages/StaticPage"));
const PackageForwardingPage = lazy(() => import("./pages/PackageForwarding"));
const PersonalShopperPage = lazy(() => import("./pages/PersonalShopper"));
const FulfillmentPage = lazy(() => import("./pages/Fulfillment"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorks"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const FAQPage = lazy(() => import("./pages/FAQ"));
const ShippingCalculatorPage = lazy(() => import("./pages/ShippingCalculator"));
const ShopFromIndiaUSA = lazy(() => import("./pages/ShopFromIndiaUSA"));
const ShopFromIndiaCanada = lazy(() => import("./pages/ShopFromIndiaCanada"));
const ShopFromIndiaUK = lazy(() => import("./pages/ShopFromIndiaUK"));
const ShopFromIndiaAustralia = lazy(() => import("./pages/ShopFromIndiaAustralia"));
const ShopFromIndiaUAE = lazy(() => import("./pages/ShopFromIndiaUAE"));
const ContactPage = lazy(() => import("./pages/Contact"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));

import { useGa4Loader, usePageView } from "./lib/analytics";

/** Apply favicon + page title from server-side platform config on mount. */
function useDynamicBranding() {
  useEffect(() => {
    // First-party favicon immediately (index.html also sets this)
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    if (!link.getAttribute('href')) {
      link.href = DEFAULT_FAVICON_URL;
    }

    let cancelled = false;
    (async () => {
      try {
        const r = await getPublicConfig();
        if (cancelled) return;
        const fav = resolveBrandAssetUrl(r.data?.favicon_url, DEFAULT_FAVICON_URL);
        const name = r.data?.company_name;
        if (fav) {
          link.href = fav;
        }
        if (name && !document.title.includes(name)) {
          document.title = name;
        }
      } catch { /* silent — branding is non-critical */ }
    })();
    return () => { cancelled = true; };
  }, []);
}

function LazyFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFCFB]">
      <div className="w-8 h-8 border-3 border-[#EBE8E4] border-t-[#D96245] rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  useGa4Loader();
  usePageView();
  useDynamicBranding();

  // Scroll to top on route change (multi-page UX)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [location.pathname]);

  // Phase 10: Supabase OAuth lands on /auth/callback?code=...
  // Legacy fallback for any in-flight Emergent redirect (#session_id=...) — clears within minutes of deploy.
  if (location.pathname === '/auth/callback' || location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/verify-email" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/legal/:slug" element={<StaticPage />} />
        <Route path="/package-forwarding" element={<PackageForwardingPage />} />
        <Route path="/personal-shopper" element={<PersonalShopperPage />} />
        <Route path="/fulfillment" element={<FulfillmentPage />} />
        {flPublicRoutes}
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/shipping-calculator" element={<ShippingCalculatorPage />} />
        <Route path="/shop-from-india-usa" element={<ShopFromIndiaUSA />} />
        <Route path="/shop-from-india-canada" element={<ShopFromIndiaCanada />} />
        <Route path="/shop-from-india-uk" element={<ShopFromIndiaUK />} />
        <Route path="/shop-from-india-australia" element={<ShopFromIndiaAustralia />} />
        <Route path="/shop-from-india-uae" element={<ShopFromIndiaUAE />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/warehouse" element={<Navigate to="/dashboard?tab=warehouse" replace />} />
        <Route path="/admin" element={<Navigate to="/dashboard?tab=admin-users" replace />} />
        {flAppRoutes}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <CurrencyProvider>
                <AppRouter />
                <Toaster richColors position="top-right" closeButton />
              </CurrencyProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
